import { CaseParty } from "@/components/cases/case-party";
import { Avatar, AvatarFallback } from "@/components/core/avatar.tsx";
import { Button } from "@/components/core/buttons/button";
import DataGrid from "@/components/core/data-grid";
import DataTable from "@/components/core/data-table";
import { useModal } from "@/components/core/modals/use-modal";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import CaseOverview from "@/features/case-classification/case-overview";
import { caseActivityModelQueryOptions, useCaseActivityStatusMutation } from "@/features/case-evaluation/api/queries/acitivities-query";
import CasePaymentViewModal from "@/features/case-evaluation/components/case-payment-view-modal";
import { caseRecordQueryOptions } from "@/features/case-record/api/queries/case-records-query";
import { CaseBlock, CaseBlockBody, CaseBlockHead, CaseLayout } from "@/features/new-case-ui/case-layout";
import { queryClient } from "@/query-client";
import { formatDate } from "@/utils/format-date";
import { IconCalendarDot, IconChevronDown, IconMessageDots, IconPencilQuestion, IconScale, IconStarFilled } from "@tabler/icons-react";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import clsx from "clsx";

export const Route = createFileRoute("/_backoffice/case-payments_/$caseId")({
	loader: ({ context: { queryClient }, params }) => {
		return queryClient.ensureQueryData(caseRecordQueryOptions(params.caseId));
	},
	component: RouteComponent,
});

function RouteComponent() {
	const caseActivitiesMutation = useCaseActivityStatusMutation();
	const params = Route.useParams();
	const caseQuery = useSuspenseQuery(caseRecordQueryOptions(params.caseId));
	const queryParams = new URLSearchParams(window.location.search);
	const id = queryParams.get("id");

	const activityQuery = useSuspenseQuery(caseActivityModelQueryOptions(params.caseId, id));

	const activity = activityQuery.data;

	const data = caseQuery.data;
	const modal = useModal();

	const caseOverviewColumns: ColumnDef<CourtCase>[] = [
		{
			id: "case_category",
			label: "Case Category",
			cell: ({ data }) => <span>{data.case_category}</span>,
		},
		{
			id: "status",
			label: "Status",
			cell: ({ data }) => <span className="uppercase">{data.status}</span>,
		},
		{
			id: "stage",
			label: "Stage",
			cell: ({ data }) => <span className="uppercase">{data.stage}</span>,
		},
		{
			id: "case_type",
			label: "Case Type",
			cell: ({ data }) => <span>{data.case_type}</span>,
		},
		{
			id: "court_category",
			label: "Court Category",
			cell: ({ data }) => <span>{data.court_category}</span>,
		},
		{
			id: "court_level",
			label: "Court Level",
			cell: ({ data }) => <span>{data.court_level}</span>,
		},
		{
			id: "region",
			label: "Region",
			cell: ({ data }) => <span>{data.region}</span>,
		},
		{
			id: "province",
			label: "Province",
			cell: ({ data }) => <span>{data.province}</span>,
		},
		{
			id: "station",
			label: "Court Station/Office",
			cell: ({ data }) => <span>{data.station}</span>,
		},
		{
			id: "date_filed",
			label: "Date Filed",
			cell: ({ data }) => <span>{formatDate(data.filed_at, "PPp")}</span>,
		},
		{
			id: "date_raffled",
			label: "Date Raffled",
			cell: ({ data }) => <span>{formatDate(data.raffled_at, "PPp")}</span>,
		},
		{
			id: "date_received",
			label: "Date Received",
			cell: ({ data }) => <span>{formatDate(data.received_at, "PPp")}</span>,
		},
		{
			id: "received_by",
			label: "Received By",
			cell: ({ data }) => <span>{data.received_by?.full_name}</span>,
		},
	];

	const openFeeModal = (activity) => {
		modal.openModal({
			allowClose: true,
			title: "Case Payment Information",
			size: "sm",
			showHeader: true,
			showFooter: false,
			component: <CasePaymentViewModal data={activity} />,
		});
	};

	const openConfirmModal = () => {
		const modalId = modal.openModal({
			type: "confirm",
			variant: "green",
			title: "Confirm Payment",
			onConfirm: async () => {
				const values = {
					id: activity.id,
					status: "confirmed",
				};
				try {
					await caseActivitiesMutation.mutateAsync(values);
					await queryClient.invalidateQueries({ queryKey: ["case_activity"] });
					await queryClient.invalidateQueries({
						queryKey: ["case_activities"],
					});
					modal.closeModal(modalId);
				} catch (error) {}
				modal.closeModal(modalId);
			},
		});
	};

	type Activity = {
		id: string;
		activity: string;
		stage: string;
	};

	return (
		<Content pageName="Pending Payment">
			{/*<Header title="Pending Payment" />*/}
			<div className="flex flex-col w-full max-w-base">
				<div className="flex flex-col w-full p-6">
					<div className="flex flex-col w-full gap-4">
						<div className="flex flex-col w-full gap-2">
							<div className="flex flex-col w-full">
								<span className="font-bold text-xl text-foreground-dark">Case No. {data.case_number}</span>
								<span className="text-base text-foreground/70">{data.case_title}</span>
							</div>
						</div>

						<CaseLayout>
							<CaseBlock>
								<CaseBlockHead>
									<div className="flex flex-col w-auto">
										<span className="text-base font-bold text-foreground-dark">Case Parties</span>
									</div>
								</CaseBlockHead>
								<CaseBlockBody className="border border-border/50">
									<div className="flex flex-col w-full">
										<CaseParty type="plaintiff" caseData={data} partyData={data.plaintiffs} showInvitationCode={false} />
										<CaseParty type="defendant" caseData={data} partyData={data.case_defendants} showInvitationCode={false} />
									</div>
								</CaseBlockBody>
							</CaseBlock>
							<CaseBlock>
								<CaseBlockHead>
									<div className="flex flex-col w-auto">
										<span className="text-base font-bold text-foreground-dark">Case Overview</span>
									</div>
								</CaseBlockHead>
								<CaseBlockBody className="border border-border/50">
									<DataGrid data={data} columns={caseOverviewColumns} />
								</CaseBlockBody>
							</CaseBlock>
						</CaseLayout>
						<div className="flex flex-col w-full p-6">
							<div className="flex flex-col w-full">
								<div className="relative flex flex-col w-full rounded-md border border-border/50 overflow-hidden">
									{activity?.with_fees === true && activity?.status === null && (
										<div className="flex flex-row justify-center w-full p-1 bg-orange-100 text-orange-600">
											<span className="text-xs uppercase font-bold">Pending Payment</span>
										</div>
									)}

									<div className="flex flex-row justify-between w-full p-4 gap-3">
										<Avatar
											className={clsx(
												"w-[44px] h-[44px] rounded-md",
												activity.party_type === "bcc" ? "bg-violet-500" : activity.party_type === "Plaintiff" ? "bg-red-500" : "bg-blue-500",
											)}
										>
											<AvatarFallback className="text-base">{activity.party_type === "bcc" ? <IconScale /> : activity.party_type?.charAt(0)}</AvatarFallback>
										</Avatar>
										<div className="flex flex-col w-full gap-3">
											<div className="flex flex-row justify-between w-full gap-4">
												<div className="inline-fxlex flex-col w-full">
													{activity.due_at && (
														<p className="text-base text-foreground">
															{activity.author?.full_name} Posted on {activity.created_at}{" "}
															{activity?.with_fees === true && (
																<Button
																	variant="link"
																	className="cursor-pointer text-base text-foreground pl-0 font-semibold"
																	onClick={() => {
																		openFeeModal(activity);
																	}}
																>
																	- with fees
																</Button>
															)}
														</p>
													)}
													{activity.due_at ? (
														<div className="flex flex-col w-auto gap-1">
															<div className="flex flex-row items-center w-auto gap-2">
																<span className="text-base text-foreground-dark font-bold">{activity.activity}</span>
															</div>
															<div className="flex flex-row items-center w-auto gap-2">
																<IconCalendarDot className="text-foreground/50" size={18} stroke={1.5} />
																<span className="text-foreground text-base">Due on {formatDate(activity.due_at)}</span>
															</div>
															{activity.remarks && (
																<div className="flex flex-row items-center w-auto gap-2">
																	<IconMessageDots className="text-foreground/50" size={18} stroke={1.5} />
																	<span className="text-foreground text-base">{activity.remarks}</span>
																</div>
															)}
														</div>
													) : (
														<p className="text-base text-foreground">
															{!activity.due_at && (
																<span className="text-base text-foreground">
																	{activity.author?.full_name} Posted on {activity.created_at}{" "}
																	{activity?.with_fees === true && (
																		<Button
																			variant="link"
																			className="cursor-pointer font-semibold pl-0 text-base text-foreground"
																			onClick={() => {
																				openFeeModal(activity);
																			}}
																		>
																			- with fees
																		</Button>
																	)}
																</span>
															)}
															<div className="flex flex-row items-center w-auto gap-2">
																<span className="text-foreground-dark font-bold">{activity.activity}</span>
															</div>
															{activity.remarks && (
																<div className="flex flex-row items-center w-auto gap-2">
																	<IconMessageDots className="text-foreground/50" size={18} stroke={1.5} />
																	<span className="text-foreground text-base">{activity.remarks}</span>
																</div>
															)}
														</p>
													)}
												</div>
											</div>
											{activity?.status === null && (
												<div className="flex flex-row items-center w-full mt-2 gap-1">
													<Button
														type="button"
														size="sm"
														variant="green"
														onClick={() => {
															openConfirmModal();
														}}
													>
														<span className="">Confirm</span>
													</Button>
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Content>
	);
}
